import { Controller } from "@hotwired/stimulus";

declare var Handsontable: any;

export default class extends Controller {
  static targets = ["table", "addRowButton", "clearRowsButton"];
  static values = {
    columns: Array,
    data: Array,
    maxVisibleRows: Number,
    tableHeight: Number
  }

  declare readonly tableTarget: HTMLElement;
  declare readonly addRowButtonTarget: HTMLButtonElement;
  declare readonly clearRowsButtonTarget: HTMLButtonElement;

  declare readonly columnsValue: Array<{ key: string; header: string; width?: number }>;
  declare readonly dataValue: Array<Record<string, any>>;
  declare readonly maxVisibleRowsValue: number;
  declare readonly tableHeightValue: number;

  hot: any = null;

  readonly ROW_HEIGHT = 23;
  readonly HEADER_HEIGHT = 28;
  gatherListener: any = null;


  connect() {
    this.activate();

    this.tableTarget.addEventListener("keydown", this.handlePasteShortcut);

    this.element.addEventListener("handsontable:errors", (event: Event) => {
      const customEvent = event as CustomEvent<{ errors: any[] }>;
      this.handleErrorsEvent(customEvent);
    });
  }

  disconnect() {
    if (this.gatherListener) {
      document.removeEventListener("gatherHandsontableData", this.gatherListener);
      this.gatherListener = null;
    }

    if (this.hot) {
      this.hot.destroy();
      this.hot = null;
    }

    if (this.tableTarget) {
      this.tableTarget.removeEventListener("keydown", this.handlePasteShortcut);
    }
  }

  activate() {
    if (this.hot) return;

    const visibleRows = this.getVisibleRowCount();
    const initialRows = Math.max(this.dataValue.length, visibleRows);

    const data = [...this.dataValue];
    while (data.length < initialRows) {
      const emptyRow: Record<string, any> = {};
      this.columnsValue.forEach(c => emptyRow[c.key] = "");
      data.push(emptyRow);
    }

    this.hot = new Handsontable(this.tableTarget, {
      data: data,
      colHeaders: this.columnsValue.map(c => c.header),
      columns: this.columnsValue.map(c => ({ data: c.key, ...(c.width ? { width: c.width } : {}) })),

      rowHeaders: (index: number) => index + 1,
      contextMenu: true,
      autoWrapRow: true,
      stretchH: 'all',
      rowHeights: this.ROW_HEIGHT,
      height: this.tableHeightValue,
      fixedRowsTop: 0,
      copyPaste: true,
      pasteMode: 'overwrite',
      trimWhitespace: true,

      minRows: initialRows,
      minSpareRows: 0,

      afterLoadData: () => {
        this.ensureRowCount();
      },

      afterCreateRow: () => {
        this.ensureRowCount();
      },

      afterPaste: (data: any[][], coords: any[]) => {
        const pastedRows = data.length;
        const currentRows = this.hot.countRows();
        if (pastedRows > currentRows) {
          this.hot.alter('insert_row', currentRows, pastedRows - currentRows);
        }
      }
    });

    this.gatherListener = this.gatherData.bind(this);
    document.addEventListener("gatherHandsontableData", this.gatherListener);
  }

  handlePasteShortcut = (event: KeyboardEvent) => {
    if (event.ctrlKey && event.key === "v" && !event.shiftKey) {
      event.preventDefault();
      window.alert("Use Ctrl+Shift+V to paste without formatting. Regular Ctrl+V is disabled to prevent broken pastes.");
    }
  }

  handleErrorsEvent = (event: CustomEvent) => {
    const { errors } = event.detail;

    this.clearHighlights();

    errors.forEach((error: { rows: any, field: any, message: any }) => {
      const colIndex = this.columnsValue.findIndex(c => c.key === error.field);

      if (colIndex < 0) return;

      error.rows.forEach((rowNum: number) => {
        rowNum = rowNum - 1;
        this.hot.setCellMeta(rowNum, colIndex, "className", "hot-error");
      });
    });

    this.hot.render();
  }

  gatherData() {
    const allRows = this.getData();

    const nonEmptyRows = allRows.filter(row =>
      Object.values(row).some(value => value !== null && value !== undefined && value !== "")
    );

    const data = { rows: nonEmptyRows };

    const event = new CustomEvent("handsontable:data", {
      detail: data,
      bubbles: true
    });
    this.element.dispatchEvent(event);

    this.clearHighlights();
    this.refresh();
  }

  getVisibleRowCount(): number {
    const rowsThatFit = Math.floor((this.tableHeightValue - this.HEADER_HEIGHT) / this.ROW_HEIGHT);
    return Math.min(rowsThatFit, this.maxVisibleRowsValue);
  }

  ensureRowCount() {
    if (!this.hot) return;

    const visibleRows = this.getVisibleRowCount();
    const currentRows = this.hot.countRows();

    if (currentRows < visibleRows) {
      this.hot.alter('insert_row', currentRows, visibleRows - currentRows);
    }
  }

  getData(): Array<Record<string, any>> {
    return this.hot?.getSourceData() ?? [];
  }

  refresh() {
    if (this.hot) {
      this.hot.render();
    }
  }

  clearHighlights() {
    this.hot.getData().forEach((_row: any, rowIndex: any) => {
      this.columnsValue.forEach((_col, colIndex) => {
        this.hot.setCellMeta(rowIndex, colIndex, "className", "");
      });
    });
  }

  clearRows() {
    var rowsWithData = this.getData().filter(row =>
      Object.values(row).some(value => value !== null && value !== undefined && value !== "")
    ).length;

    if (rowsWithData > 0) {
      var confirmed = window.confirm(`This will clear all ${rowsWithData} rows with data. Are you sure?`);
      if (!confirmed) return;
    }

    var data = Array(this.hot.countRows()).fill(null).map(() => {
      const emptyRow: Record<string, any> = {};
      this.columnsValue.forEach(c => emptyRow[c.key] = "");
      return emptyRow;
    });

    this.hot.loadData(data);
    this.refresh();
  }
}
